import type { ListDocumentsQuery } from './types'

/** Filters as the documents list sends them; part of the list's query key. */
export type DocumentListFilters = Pick<ListDocumentsQuery, 'statusGroup' | 'targetLang' | 'q' | 'sort'>

/**
 * One place for every query key (03-frontend §5.2). Keys under ['documents', id, ...] belong to one
 * document, so invalidating ['documents', id] refreshes everything about it; lists live under
 * ['documents', 'list'] and are unaffected by detail invalidations.
 */
export const qk = {
  me: ['me'] as const,
  authConfig: ['auth', 'config'] as const,
  languages: ['languages'] as const,
  clientConfig: ['config'] as const,
  companyGlossaries: (sourceLang: string | null, targetLang: string) =>
    ['company-glossaries', sourceLang ?? 'auto', targetLang] as const,
  documents: {
    all: ['documents'] as const,
    lists: ['documents', 'list'] as const,
    list: (filters: DocumentListFilters) => ['documents', 'list', filters] as const,
    detail: (id: string) => ['documents', id] as const,
    pages: (id: string) => ['documents', id, 'pages'] as const,
    state: (id: string) => ['documents', id, 'state'] as const,
    preparation: (id: string) => ['documents', id, 'preparation'] as const,
    runs: (id: string) => ['documents', id, 'runs'] as const,
    run: (id: string, runId: string) => ['documents', id, 'runs', runId] as const,
    jobs: (id: string) => ['documents', id, 'jobs'] as const,
    glossary: (id: string) => ['documents', id, 'glossary'] as const,
    suggestions: (id: string) => ['documents', id, 'suggestions'] as const,
    images: (id: string) => ['documents', id, 'images'] as const,
    image: (id: string, imageId: number) => ['documents', id, 'images', imageId] as const,
    issues: (id: string) => ['documents', id, 'issues'] as const,
    translationSummary: (id: string) => ['documents', id, 'translation-summary'] as const,
    /** Immutable per version: a new version is a new key, so these are never invalidated. */
    elements: (id: string, pageId: number, version: number) => ['documents', id, 'elements', pageId, version] as const,
    results: (id: string, pageId: number, outputVersion: number) =>
      ['documents', id, 'results', pageId, outputVersion] as const,
    textIndex: (id: string, version: number) => ['documents', id, 'text-index', version] as const,
    element: (id: string, eid: number) => ['documents', id, 'element', eid] as const,
  },
  admin: {
    all: ['admin'] as const,
    users: (q: string) => ['admin', 'users', q] as const,
    user: (userId: string) => ['admin', 'user', userId] as const,
    deleted: ['admin', 'deleted-documents'] as const,
  },
  glossaries: {
    all: ['company-glossaries'] as const,
    list: ['company-glossaries', 'all'] as const,
    detail: (glossaryId: string) => ['company-glossary', glossaryId] as const,
    terms: (glossaryId: string, q: string) => ['company-glossary', glossaryId, 'terms', q] as const,
  },
} as const

/**
 * Keys whose resync is exact: the document detail sits at the root of every document key, so a
 * prefix match would refetch all of them (pages, rasters' metadata, immutable elements).
 */
const EXACT_SCOPES = new Set(['document'])

export function resyncFilter(scope: string, id: string): { queryKey: readonly unknown[]; exact: boolean } | null {
  const key = RESYNC_SCOPES[scope]
  return key ? { queryKey: key(id), exact: EXACT_SCOPES.has(scope) } : null
}

/** `resync.scope` names (02-api §12.1) mapped to the document sub-keys they cover. */
export const RESYNC_SCOPES: Record<string, (id: string) => readonly unknown[]> = {
  document: qk.documents.detail,
  pages: qk.documents.pages,
  state: qk.documents.state,
  issues: qk.documents.issues,
  images: qk.documents.images,
  glossary: qk.documents.glossary,
  suggestions: qk.documents.suggestions,
  preparation: qk.documents.preparation,
  runs: qk.documents.runs,
  jobs: qk.documents.jobs,
}
